import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getComicById, getRelatedComics } from '@/mocks/comics';

const categoryNames: Record<string, string> = {
  romance: '恋爱',
  fantasy: '奇幻',
  action: '动作',
  comedy: '搞笑',
  thriller: '悬疑',
  horror: '恐怖',
  drama: '剧情',
  daily: '日常',
};

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const [showAllChapters, setShowAllChapters] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const comic = getComicById(Number(id));

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6 flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 flex items-center justify-center bg-background-100 rounded-full mb-4">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="text-lg font-medium text-foreground-700 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">该漫画不存在或已被删除</p>
            <Link
              to="/"
              className="px-4 py-2 text-sm bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const related = getRelatedComics(comic.id, 6);
  const displayedChapters = showAllChapters ? comic.chapters_list : comic.chapters_list.slice(0, 6);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryNames[comic.category] || comic.category, href: `/category/${comic.category}` },
    { label: comic.title },
  ];

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: `${comic.title} - 叮咚漫画网`,
          description: comic.description,
          author: { '@type': 'Person', name: comic.author },
          datePublished: comic.updatedAt,
        })}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 md:pt-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            {/* Comic Detail Hero */}
            <div className="mt-4 flex flex-col md:flex-row gap-6 md:gap-10">
              {/* Cover */}
              <div className="flex-shrink-0 w-full sm:w-56 md:w-64">
                <div className="relative overflow-hidden rounded-lg aspect-[3/4]">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover"
                  />
                  {comic.status === '已完结' && (
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-0.5 text-xs bg-secondary-500 text-background-50 rounded-md">
                        已完结
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3 font-heading">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <div className="flex items-center gap-1 text-accent-500">
                    <i className="ri-star-fill"></i>
                    <span className="font-semibold text-foreground-900">{comic.rating}</span>
                  </div>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-eye-line mr-1"></i>{comic.views}
                  </span>
                  <span className="text-sm text-foreground-500">共{comic.chapters}话</span>
                  <span className={`px-2 py-0.5 text-xs rounded-md ${
                    comic.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                </div>

                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs text-foreground-500">作者：</span>
                  <span className="text-sm text-foreground-900 font-medium">{comic.author}</span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 text-xs bg-background-100 text-foreground-600 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {comic.description}
                </p>

                <div className="flex items-center gap-3">
                  <Link
                    to={`/article/${comic.id}`}
                    className="flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors font-medium text-sm whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-book-read-line"></i>
                    开始阅读
                  </Link>
                  <button className="w-10 h-10 flex items-center justify-center border border-background-300 rounded-lg text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer">
                    <i className="ri-heart-line text-lg"></i>
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center border border-background-300 rounded-lg text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer">
                    <i className="ri-share-forward-line text-lg"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Chapter List */}
            <section className="mt-10 mb-10" aria-label="章节列表">
              <h2 className="text-lg md:text-xl font-bold text-foreground-900 mb-4 font-heading">
                章节列表
              </h2>
              <div className="bg-background-100 rounded-lg border border-background-200 divide-y divide-background-200">
                {displayedChapters.map((ch) => (
                  <Link
                    key={ch.id}
                    to={`/article/${comic.id}`}
                    className="flex items-center justify-between px-4 py-3 hover:bg-background-50 transition-colors cursor-pointer"
                  >
                    <span className="text-sm text-foreground-900">{ch.title}</span>
                    <span className="text-xs text-foreground-500">{ch.date}</span>
                  </Link>
                ))}
              </div>
              {comic.chapters_list.length > 6 && (
                <button
                  onClick={() => setShowAllChapters(!showAllChapters)}
                  className="mt-4 w-full py-3 text-sm text-foreground-500 hover:text-primary-600 bg-background-100 hover:bg-background-200 rounded-lg transition-colors cursor-pointer"
                >
                  {showAllChapters ? '收起' : `查看全部 ${comic.chapters_list.length} 话`}
                </button>
              )}
            </section>

            {/* Related Comics */}
            {related.length > 0 && (
              <section className="py-10 border-t border-background-200" aria-label="相关推荐">
                <h2 className="text-lg md:text-xl font-bold text-foreground-900 mb-6 font-heading">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {related.map((comic) => (
                    <ComicCard key={comic.id} {...comic} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}